export type ColumnMeta = {
  name: string;
  role_guess?: 'numeric' | 'categorical' | 'boolean' | 'datetime' | 'text' | 'id';
  count?: number;
  missing?: number;
  unique?: number;
  numeric?: { std?: number } | null;
  categorical?: { cardinality?: number; entropy?: number | null } | null;
};

export type RankRole = 'x' | 'y' | 'series' | 'facet';

export type RankHints = {
  // Signals for ordering only
  recentlyUsed?: Set<string>;
  userPinned?: Set<string>;
  chartId?: string | null;
};

// Heuristics for type compatibility per role
function expectedTypeForRole(role: RankRole): 'any' | 'numeric' | 'categoricalOrDatetime' {
  if (role === 'y') return 'numeric';
  if (role === 'series' || role === 'facet') return 'categoricalOrDatetime';
  return 'any'; // x can be either; will bias by chart later
}

function normalizeNameScore(name: string): number {
  // penalize id-like names
  const s = name.toLowerCase();
  if (/\b(id|uuid|guid|key|index)\b/.test(s)) return -1;
  if (/^id$/.test(s.trim())) return -1.5;
  return 0;
}

function typeMatchScore(meta: ColumnMeta | undefined, need: ReturnType<typeof expectedTypeForRole>): number {
  if (!meta || need === 'any') return 0.2; // slight positive
  const g = meta.role_guess;
  if (need === 'numeric') return g === 'numeric' ? 1.0 : (g === 'boolean' ? 0.2 : -0.4);
  if (need === 'categoricalOrDatetime') return (g === 'categorical' || g === 'boolean' || g === 'datetime') ? 0.8 : -0.3;
  return 0;
}

function convertibilityScore(meta: ColumnMeta | undefined, role: RankRole): number {
  if (!meta) return 0;
  // numeric convertible to categorical via binning for facet/series (small boost)
  if ((role === 'series' || role === 'facet') && meta.role_guess === 'numeric') return 0.2;
  // categorical convertible to x (labels) small boost
  if (role === 'x' && (meta.role_guess === 'categorical' || meta.role_guess === 'datetime' || meta.role_guess === 'boolean')) return 0.3;
  return 0;
}

function cardinalityFitScore(meta: ColumnMeta | undefined, role: RankRole): number {
  if (!meta) return 0;
  const card = meta.categorical?.cardinality ?? meta.unique ?? null;
  if (role === 'series') {
    if (card == null) return 0.1;
    if (card <= 12) return 0.6;
    if (card <= 50) return 0.2;
    return -0.4;
  }
  if (role === 'facet') {
    if (card == null) return 0.1;
    if (card <= 8) return 0.5;
    if (card <= 24) return 0.1;
    return -0.3;
  }
  if (role === 'x') {
    if (meta.role_guess === 'datetime') return 0.6;
    if (card == null) return 0.1;
    if (card <= 50) return 0.3;
    if (card <= 200) return 0.1;
    return -0.2;
  }
  if (role === 'y') {
    // prefer numeric with variation
    return 0.3;
  }
  return 0;
}

function varianceEntropyScore(meta: ColumnMeta | undefined): number {
  if (!meta) return 0;
  if (meta.role_guess === 'numeric') {
    const std = meta.numeric?.std ?? 0;
    if (std > 0) return Math.min(0.6, Math.log10(1 + std) / 3);
    return -0.2;
  }
  if (meta.role_guess === 'categorical' || meta.role_guess === 'boolean') {
    const card = meta.categorical?.cardinality ?? meta.unique ?? 0;
    if (card <= 1) return -0.3;
    if (card <= 12) return 0.3;
    return 0.1;
  }
  return 0.05;
}

export function rankColumnsForRole(
  columns: Array<{ name: string } | string>,
  role: RankRole,
  metaByName?: Record<string, ColumnMeta>,
  hints?: RankHints
): string[] {
  const names = columns.map((c) => typeof c === 'string' ? c : c.name).filter(Boolean);
  const need = expectedTypeForRole(role);
  const recently = hints?.recentlyUsed ?? new Set<string>();
  const pinned = hints?.userPinned ?? new Set<string>();

  const scored = names.map((name) => {
    const meta = metaByName?.[name];
    const score =
      typeMatchScore(meta, need) +
      convertibilityScore(meta, role) +
      cardinalityFitScore(meta, role) +
      varianceEntropyScore(meta) +
      (normalizeNameScore(name)) +
      (recently.has(name) ? 0.4 : 0) +
      (pinned.has(name) ? 0.8 : 0);
    return { name, score };
  });

  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return scored.map((s) => s.name);
}

export default rankColumnsForRole;


